import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { EntityContent } from 'src/content/entity-content.js';
import 'src/utilities/display-alert';
import '../../library/editors/index.js';
import '../../library/editors/components/d-edit-access.js';
import '../../library/editors/elements/d-select-dropdown';
import '../../library/fields/index.js';
import '../../library/lists/d-list-section-attachment.js';
import { t } from 'src/i18n';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import { validateNorwegianIdNumber } from 'norwegian-national-id-validator';
import { accessProfiles } from 'src/store/selectors';
import type { UpdateSectionItem } from '../update-section-item';
import type { ContactView } from './contact-view';
import type { ContactViewEditItem } from './contact-view-edit-item';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import type { UserForAccess } from '../../library/editors/components/user-for-access';
import type { ListDefinition } from '../list-definition';
import type { SectionField } from '../../library/components/section-field';

/**
 *
 */
@customElement('d-contact-view')
export class DContactView extends EntityContent<ContactView, ContactViewEditItem> {
  static readonly styles = EntityContent.styles;
  @property({ type: String })
  entityType = 'contacts';
  @property({ type: String })
  parentEntityType = 'partners';
  @property({ type: String })
  currentUserEmail = '';

  static get observers() {
    return ['_nameChanged(editItem.firstName,editItem.lastName)'];
  }

  private get accessLevelOptions(): SelectDropdownOption[] {
    return accessProfiles().map((a) => {
      return { value: a.name, text: a.displayName };
    });
  }

  private get noName(): boolean {
    return !!(
      this.editItem &&
      isEmptyOrInvalidString(this.editItem.firstName) &&
      isEmptyOrInvalidString(this.editItem.lastName)
    );
  }

  _isCurrentUser(currentUserEmail, email) {
    return currentUserEmail === email;
  }

  doneDisabled(): boolean {
    // Check existing validation
    if (this.noName || this.entityView.unavailableEmails.includes(this.editItem?.email ?? '')) {
      return true;
    }

    // Check if personal identifier is invalid (not empty and not valid)
    if (this.editItem?.personalIdentifier) {
      const cleanedValue = this.editItem.personalIdentifier.replace(/[\s.-]/g, '');
      if (cleanedValue && !validateNorwegianIdNumber(cleanedValue)) {
        return true; // Disable the Done button
      }
    }
    return false;
  }

  async initializeEditItem() {
    this.editItem = {
      firstName: this.entityView.firstName,
      lastName: this.entityView.lastName,
      personalIdentifier: this.entityView.personalIdentifier,
      accessLevel: this.entityView.accessLevel || 'NONE',
      email: this.entityView.email,
      mobilePhone: this.entityView.mobilePhone,
      notes: this.entityView.notes,
      telephone: this.entityView.telephone,
      name: this.entityView.name,
      partnerId: this.entityView.parentEntityId,
      accessExpires: this.entityView.accessExpires,
    };
  }

  editUser(item: ContactViewEditItem): UserForAccess {
    return {
      uuid: this.entityView.uuid,
      email: item.email,
      accessLevel: item.accessLevel,
      accessExpires: item.accessExpires,
      name: this.entityView.name,
      partnerName: this.entityView.partnerName,
      partnerUuid: this.entityView.partnerUuid,
    };
  }

  protected onFieldSectionChanged(e: CustomEvent<{ value: ContactViewEditItem }>) {
    const item = e.detail.value;

    // Clean up personal identifier if it exists
    if (item.personalIdentifier) {
      const cleanedValue = item.personalIdentifier.replace(/[\s.-]/g, '');
      if (cleanedValue !== item.personalIdentifier) {
        this.editItem = { ...item, personalIdentifier: cleanedValue };
        return;
      }
    }

    // Update the edit item
    this.editItem = item;
  }

  protected async saveEditItem(item: ContactViewEditItem): Promise<ContactViewEditItem | undefined> {
    // Double-check validation before saving
    if (item.personalIdentifier) {
      const cleanedValue = item.personalIdentifier.replace(/[\s.-]/g, '');
      if (cleanedValue && !validateNorwegianIdNumber(cleanedValue)) {
        alert(t('employees.personalIdentifierMustBe11Digits'));
        return undefined; // Prevent saving
      }
    }
    return item;
  }

  renderViewMode() {
    return html` ${this.renderFieldSection('contacts', this.viewFields(), this.entityView)} `;
  }

  renderEditAccess(item: ContactViewEditItem): TemplateResult<1> {
    return html`
      <d-edit-access
        style="margin-top: 0px; border: none; box-shadow: none;"
        disabled="${this._isCurrentUser(this.currentUserEmail, item.email)}"
        email-value="${item.email}"
        field="contacts_accessLevel"
        .value=${item.accessLevel}
        .editUser=${this.editUser(item)}
        .otherUserEmails=${[]}
        .currentUserUuid=${'TODO'}
        @value-changed=${(e: CustomEvent<{ accessLevel: 'NONE' | 'USER'; email: string; accessExpires: string }>) => {
          this.editItem = {
            ...item,
            accessLevel: e.detail.accessLevel,
            accessExpires: e.detail.accessExpires,
            email: e.detail.email,
          };
        }}
      >
      </d-edit-access>
    `;
  }

  renderEditItem(item: ContactViewEditItem): TemplateResult<1> {
    return this.renderFieldSection('contacts', this.editFields(item), item);
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  protected viewFields(): SectionField[] {
    const m = this.entityView.featureStates;
    return [
      {
        field: 'partnerName',
        type: 'view-text',
        class: 'fullWidth',
      },
      {
        field: 'mobilePhone',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'telephone',
        type: 'view-text',
        class: 'minWidth200',
        condition: m.core || m.hse,
      },
      {
        field: 'email',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'accessLevel',
        type: 'view-text',
        options: this.accessLevelOptions,
        class: 'minWidth200',
        condition: m.core || m.hse,
      },
      {
        field: 'personalIdentifier',
        fieldForLabel: 'employees_nationalIdentityNumber',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'notes',
        type: 'view-text',
        class: 'fullWidth',
        condition: m.core || m.hse || m.data,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  private editFields(item: ContactViewEditItem): SectionField[] {
    const m = this.entityView.featureStates;
    return [
      {
        field: 'firstName',
        type: 'edit-text',
        class: 'minWidth300',
        autofocus: !this.entityView.isConfirmedEntity,
        selectOnFocus: !this.entityView.isConfirmedEntity,
        markIfEmpty: true,
        placeholder: this.noName ? t('employees.personMustHaveName') : '',
      },
      {
        field: 'lastName',
        type: 'edit-text',
        class: 'minWidth300',
        markIfEmpty: true,
        placeholder: this.noName ? t('employees.personMustHaveName') : '',
      },
      {
        field: 'mobilePhone',
        type: 'edit-text',
        class: 'minWidth200',
      },
      {
        field: 'telephone',
        type: 'edit-text',
        class: 'minWidth200',
        condition: m.core || m.hse,
      },
      {
        field: 'email',
        type: 'edit-text',
        class: 'minWidth200',
        condition: !m.core && !m.hse,
      },
      {
        type: 'custom',
        render: () => {
          return this.renderEditAccess(item);
        },
        condition: m.core || m.hse,
      },
      {
        type: 'view-info',
        field: '',
        content: t('contacts.emailInUse'),
        condition: this.entityView.unavailableEmails.includes(this.editItem?.email ?? ''),
      },
      {
        field: 'notes',
        type: 'edit-textarea',
        class: 'fullWidth',
        condition: m.core || m.hse || m.data,
      },
      {
        field: 'personalIdentifier',
        label: t('employees.nationalIdNumber'),
        type: 'edit-text',
        class: 'minWidth200',
        placeholder: t('employees.elevenDigitsOptional'),
        underLabel:
          this.editItem?.personalIdentifier && !validateNorwegianIdNumber(this.editItem.personalIdentifier)
            ? '<span style="color: var(--alertColor)">' + t('employees.personalIdentifierMustBe11Digits') + '</span>'
            : undefined,
      },
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-contact-view': DContactView;
  }
}
